import { useState, useEffect, useCallback, useRef } from 'react';
import { useTelegram } from '@/hooks/useTelegram';
import HeroSection from '@/sections/HeroSection';
import ServicesSection from '@/sections/ServicesSection';
import CalculatorSection from '@/sections/CalculatorSection';
import ProcessSection from '@/sections/ProcessSection';
import PortfolioSection from '@/sections/PortfolioSection';
import InfoBlocksSection from '@/sections/InfoBlocksSection';
import FAQSection from '@/sections/FAQSection';
import OrderModal from '@/sections/OrderModal';
import BottomNav from '@/sections/BottomNav';
import LanguageSwitcher from '@/sections/LanguageSwitcher';
import AccountButton from '@/sections/AccountButton';

const SECTION_IDS = ['hero', 'services', 'calculator', 'portfolio', 'faq'] as const;

function App() {
  const { haptic, showMainButton, hideMainButton, isInTelegram } = useTelegram();
  const [activeSection, setActiveSection] = useState<string>('hero');
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderPrice, setOrderPrice] = useState(0);
  const rafRef = useRef<number | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);

  // Single scroll listener: tracks active section + updates progress bar via CSS.
  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const scrollTop = window.scrollY;

        // Active section
        const probe = scrollTop + 200;
        for (let i = SECTION_IDS.length - 1; i >= 0; i--) {
          const el = document.getElementById(SECTION_IDS[i]);
          if (el && el.offsetTop <= probe) {
            setActiveSection(prev =>
              prev === SECTION_IDS[i] ? prev : SECTION_IDS[i]
            );
            break;
          }
        }

        // Progress bar
        const docHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        const progress = docHeight > 0 ? scrollTop / docHeight : 0;
        if (progressRef.current) {
          progressRef.current.style.width = `${progress * 100}%`;
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleNavigate = useCallback(
    (section: string) => {
      haptic('light');
      const el = document.getElementById(section);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [haptic]
  );

  const handleSelectService = useCallback(
    (id: string) => {
      haptic('light');
      setSelectedService(id);
      setTimeout(() => handleNavigate('calculator'), 250);
    },
    [haptic, handleNavigate]
  );

  const handleOrder = useCallback(
    (serviceId: string, price: number) => {
      haptic('medium');
      setSelectedService(serviceId);
      setOrderPrice(price);
      setOrderModalOpen(true);
    },
    [haptic]
  );

  const handleCloseOrder = useCallback(() => setOrderModalOpen(false), []);

  // Telegram MainButton mirrors the in-section CTA when modal is closed and a
  // service is selected. Single source of truth: when modal is open, MainButton
  // is hidden so it never competes with the form's submit button.
  useEffect(() => {
    if (orderModalOpen || !isInTelegram || !selectedService) {
      hideMainButton();
      return;
    }
    const label = orderPrice
      ? `$${orderPrice.toLocaleString()}`
      : 'Order';
    showMainButton(label, () => handleOrder(selectedService, orderPrice));
    return () => hideMainButton();
  }, [
    selectedService,
    orderPrice,
    orderModalOpen,
    isInTelegram,
    showMainButton,
    hideMainButton,
    handleOrder,
  ]);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-[#F0F2F5] relative overflow-x-hidden pb-[calc(64px+env(safe-area-inset-bottom))] lg:pb-0">
      <div className="fixed top-0 left-0 right-0 h-[2px] bg-transparent z-[60]">
        <div
          ref={progressRef}
          className="h-full bg-gradient-to-r from-[#00D4FF] to-[#00E5A0] transition-all duration-100"
          style={{ width: '0%' }}
        />
      </div>

      <LanguageSwitcher />
      <AccountButton />

      <main>
        <HeroSection onNavigate={handleNavigate} />
        <ServicesSection
          selectedService={selectedService}
          onSelectService={handleSelectService}
        />
        <CalculatorSection
          selectedServiceId={selectedService}
          onOrder={handleOrder}
        />
        <ProcessSection />
        <PortfolioSection />
        <InfoBlocksSection />
        <FAQSection />
      </main>

      <OrderModal
        isOpen={orderModalOpen}
        serviceId={selectedService}
        price={orderPrice}
        onClose={handleCloseOrder}
      />
      <BottomNav activeSection={activeSection} onNavigate={handleNavigate} />
    </div>
  );
}

export default App;
